import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import type {
  EsiidLookupRequest,
  EsiidLookupResponse,
} from '@rheora/enrollment-contract';

import { establishingFunnelSession } from '../services/funnel-session.service';
import { postEsiidLookup } from './esiid.fetchers';
import { funnelQueryKeys } from './funnel.queries';

/**
 * TanStack hook for the ESI ID lookup (SHOP-3). Pages/components call THIS, never
 * the fetcher. It is a mutation (a POST with a captcha/idempotency posture and a
 * side-effecting session record), fired when the shopper submits their ESI ID.
 *
 * Not retried on the client's account — the BFF rate-limits the lookup per
 * IP∧session, so a silent client retry would eat the free allowance.
 *
 * ⚠️ THIS IS THE MUTATION THAT CREATES THE FUNNEL. It writes the server-side
 * candidate record and moves the session's active-candidate pointer, so the cached
 * `GET /bff/funnel` snapshot is stale the instant it succeeds. Invalidating means a
 * later remount rehydrates the meter the shopper just resolved rather than the one
 * they resolved before it.
 *
 * ⚠️ IT ALSO RELEASES THE TERMINAL-SESSION LATCH. A shopper whose session lapsed
 * has a latched 401 suppressing further snapshot reads; resolving a meter mints a
 * NEW session, so the latch must let go or the rehydrate would stay dead for the
 * rest of the tab's life. See `funnel-session.service.ts`.
 */
export function useEsiidLookup(): UseMutationResult<
  EsiidLookupResponse,
  Error,
  EsiidLookupRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EsiidLookupRequest) =>
      establishingFunnelSession(() => postEsiidLookup(request)),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: funnelQueryKeys.snapshot() }),
  });
}
